import React, {
  ChangeEvent,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import {IKid} from '@/app/main/onboarding/pages/AddKid/model/AddKidMode';
import {CYearsOld} from '@/app/main/onboarding/pages/AddKid/pages/PersonalInfo/constants/PersonalInfoConstants';
import {selectUser} from '@/app/store/authSlice';
import {useAppSelector} from '@/app/store/hooks';
import {featureFlags} from '@/config/featureFlags';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {useCountryCode} from '@/hooks';
import {isUnderAge} from '@/utils/validateDates';

import {getKidData, saveKidData} from '../../services/handleKidInformation';

import {useTrackers} from './trackers';

type ViewModelContextType = {
  kidData: Partial<IKid>;
  phone: string;
  email: string;
  kidFirstName: string;
  isDisabled: boolean;
  isEmailEqualToParent: boolean;
  isPhoneEqualToParent: boolean;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setPhone: React.Dispatch<React.SetStateAction<string>>;
  isCheckedCheckbox?: boolean;
  showAccountSwitchingForm?: boolean;
  setEmailError?: React.Dispatch<React.SetStateAction<boolean>>;
  isCountryCodeVisible: boolean;
  phoneInputErrorMessage: string;
  handleContinue: () => void;
  handleBack: () => void;
  handleOnChangeCheckbox: (event: ChangeEvent<HTMLInputElement>) => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  //#region [---- [PROPERTIES] ----]
  const userData = useAppSelector(selectUser);
  const kidData = getKidData();
  const navigate = useNavigate();
  const {profile} = useUsersRepository();
  const {t} = useTranslation();
  const {
    trackSelectBackTeenInformationConfirmData,
    trackSelectAddKidEmailPhoneContinue,
    trackViewAddKidEmailPhone,
  } = useTrackers();
  const kidFirstName = kidData?.firstName || '';
  const {isCountryCodeVisible} = useCountryCode();
  //#endregion

  //#region [---- [HOOKS] ----]
  const [phone, setPhone] = useState<string>(getKidData()?.phone || '');
  const [email, setEmail] = useState<string>(getKidData()?.email || '');
  const [emailError, setEmailError] = useState<boolean>(false);
  const [isCheckedCheckbox, setIsCheckedCheckbox] = useState<boolean>(false);
  const isAccountSwitchingEnabled = featureFlags.isEnabledAccountSwitching;

  const isKidUnderAge = useMemo(() => {
    return isUnderAge(getKidData().dob, CYearsOld.TeenUnderAge);
  }, []);

  const showAccountSwitchingForm = useMemo(() => {
    return Boolean(isAccountSwitchingEnabled && isKidUnderAge);
  }, [isAccountSwitchingEnabled, isKidUnderAge]);

  const isEmailEqualToParent = useMemo(() => {
    return email === profile?.email;
  }, [email, profile?.email]);

  const isPhoneEqualToParent = useMemo(() => {
    return phone === profile?.phone;
  }, [phone, profile?.phone]);

  const phoneInputErrorMessage = useMemo(() => {
    if (isPhoneEqualToParent) {
      return t('add_kid.contact_info.invalid_phone_equal_parent');
    }
    return '';
  }, [isPhoneEqualToParent, t]);

  useEffect(() => {
    trackViewAddKidEmailPhone();
    const kidData = getKidData();
    if (kidData) {
      setEmail(kidData?.email ?? '');
      setPhone(kidData?.phone ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion

  //#region [---- [LOGIC] ----]
  const handleOnChangeCheckbox = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked;
      setIsCheckedCheckbox(value);
    },
    [],
  );

  const handleContinue = useCallback(() => {
    if (!isCheckedCheckbox) {
      saveKidData({phone, email});
    } else {
      saveKidData({phone: '', email: ''});
    }

    trackSelectAddKidEmailPhoneContinue(
      !phone,
      profile?.metadata?.ONBOARDING_CARD_THEME_SELECTED,
      userData?.id,
    );
    navigate('/onboarding/add-kid/summary');
  }, [
    email,
    isCheckedCheckbox,
    navigate,
    phone,
    profile?.metadata?.ONBOARDING_CARD_THEME_SELECTED,
    trackSelectAddKidEmailPhoneContinue,
    userData?.id,
  ]);

  const handleBack = useCallback(() => {
    saveKidData({phone, email});
    trackSelectBackTeenInformationConfirmData();
    navigate('/onboarding/add-kid/personal-info');
  }, [email, navigate, phone, trackSelectBackTeenInformationConfirmData]);
  //#endregion

  const isDisabled = useMemo(() => {
    if (isCheckedCheckbox) return false;

    return (
      !phone ||
      !email ||
      isPhoneEqualToParent ||
      isEmailEqualToParent ||
      emailError
    );
  }, [
    isCheckedCheckbox,
    phone,
    email,
    isPhoneEqualToParent,
    isEmailEqualToParent,
    emailError,
  ]);

  return (
    <ViewModelContext.Provider
      value={{
        kidData,
        phone,
        email,
        kidFirstName,
        isDisabled,
        isEmailEqualToParent,
        isPhoneEqualToParent,
        handleContinue,
        handleBack,
        setPhone,
        setEmail,
        handleOnChangeCheckbox,
        isCheckedCheckbox,
        showAccountSwitchingForm,
        setEmailError,
        isCountryCodeVisible,
        phoneInputErrorMessage,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('Contact Info ViewModelContext is undefined');
  }
  return context;
}
